import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, Chip, MenuItem, TablePagination,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { paymentApi, invoiceApi } from '../../services/api';
import { Payment, Invoice } from '../../types';

const payMethodMap: Record<string, number> = { Cash: 1, Check: 2, BankTransfer: 3, CreditCard: 4 };
const payMethodLabels = Object.keys(payMethodMap);

const PaymentList: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [invoiceHeaderId, setInvoiceHeaderId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('BankTransfer');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [payRes, invRes] = await Promise.all([
        paymentApi.getAll(),
        invoiceApi.getAll(),
      ]);
      setPayments(Array.isArray(payRes.data) ? payRes.data : payRes.data?.data || []);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : invRes.data?.data || []);
    } catch (err: any) {
      setError('Failed to load payments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddDialog = () => {
    setInvoiceHeaderId(''); setUnitId(''); setAmount(''); setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMethod('BankTransfer'); setReferenceNumber(''); setNotes(''); setFormError('');
    setDialogOpen(true);
  };

  const handleInvoiceSelect = (invId: string) => {
    setInvoiceHeaderId(invId);
    const inv = invoices.find(i => i.id === Number(invId));
    if (inv) {
      setUnitId(String(inv.unitId));
      setAmount(String(inv.balanceDue));
    }
  };

  const handleSave = async () => {
    if (!invoiceHeaderId) { setFormError('Invoice is required.'); return; }
    if (!amount || Number(amount) <= 0) { setFormError('Valid amount is required.'); return; }
    setSaving(true); setFormError('');
    try {
      await paymentApi.create({
        invoiceHeaderId: Number(invoiceHeaderId),
        unitId: Number(unitId),
        amount: Number(amount),
        paymentDate: new Date(paymentDate).toISOString(),
        paymentMethod: payMethodMap[paymentMethod] || 3,
        referenceNumber: referenceNumber.trim(),
        notes: notes.trim(),
      });
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to record payment.');
    } finally { setSaving(false); }
  };

  const formatCurrency = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const displayed = payments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Filter unpaid invoices for dropdown
  const unpaidInvoices = invoices.filter(inv => inv.status !== 'Paid' && inv.status !== 'Cancelled');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Payments</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} disabled={unpaidInvoices.length === 0}>
          Record Payment
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Invoice</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No payments recorded.</Typography></TableCell></TableRow>
              ) : displayed.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.invoiceNumber || `#${p.invoiceHeaderId}`}</Typography></TableCell>
                  <TableCell>{p.unitNumber || `Unit #${p.unitId}`}</TableCell>
                  <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                  <TableCell>{new Date(p.paymentDate).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={p.paymentMethod} size="small" variant="outlined" /></TableCell>
                  <TableCell>{p.referenceNumber || '-'}</TableCell>
                  <TableCell><Chip label={p.status} color="success" size="small" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={payments.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Invoice" select fullWidth margin="normal" value={invoiceHeaderId} onChange={e => handleInvoiceSelect(e.target.value)} required disabled={saving}>
            {unpaidInvoices.map(inv => (
              <MenuItem key={inv.id} value={String(inv.id)}>
                {inv.invoiceNumber} ({inv.unitNumber || `Unit ${inv.unitId}`}) - Balance: {formatCurrency(inv.balanceDue)}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Unit ID" fullWidth margin="normal" value={unitId} disabled />
          <TextField label="Amount" fullWidth margin="normal" value={amount} onChange={e => setAmount(e.target.value)} type="number" required disabled={saving} />
          <TextField label="Payment Date" type="date" fullWidth margin="normal" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} disabled={saving} />
          <TextField label="Payment Method" select fullWidth margin="normal" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} disabled={saving}>
            {payMethodLabels.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField label="Reference Number" fullWidth margin="normal" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} disabled={saving} />
          <TextField label="Notes" fullWidth margin="normal" value={notes} onChange={e => setNotes(e.target.value)} multiline rows={2} disabled={saving} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentList;
